import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchKitchenOrders,
  notifyKitchenOrder,
  updateKitchenOrderStatus,
  type KitchenOrder,
  type KitchenOrderItem,
  type KitchenOrderItemStatus,
} from '@/lib/api/orders';
import { useTenantStore } from '@/stores/tenantStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, safeDate } from '@/lib/utils';
import { Clock, Loader2, Package, AlertTriangle, Bell, CheckCircle } from 'lucide-react';

const ASSEMBLY_TARGET_SECONDS = 300; // 5 minutes

const ITEM_STATUS_COLORS: Record<KitchenOrderItemStatus, string> = {
  QUEUED: 'text-slate-400',
  FIRED: 'text-sky-300',
  PARTIAL_READY: 'text-amber-300',
  READY: 'text-emerald-400',
  RECALLED: 'text-rose-300',
  CANCELLED: 'text-slate-500 line-through',
};

const formatEta = (seconds: number | null | undefined): string | null => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
    return null;
  }
  if (seconds < 60) {
    return `${Math.max(0, Math.round(seconds))}s`;
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) {
    return `${hours} hr`;
  }
  return `${hours} hr ${remainingMinutes} min`;
};

const formatPlacedAt = (iso?: string | null): string => {
  if (!iso) {
    return 'Unknown';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return 'Unknown';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

type DecoratedExpoOrder = {
  order: KitchenOrder;
  totalQuantity: number;
  readyQuantity: number;
  awaitingAssembly: boolean;
  allItemsReady: boolean;
  overdue: boolean;
};

const EMPTY_KITCHEN_ORDERS: KitchenOrder[] = [];
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

type DayBounds = {
  startMs: number;
  endMs: number;
};

const computeTodayBounds = (): DayBounds => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { startMs: start.getTime(), endMs: end.getTime() };
};

const useTodayBounds = (): DayBounds => {
  const [bounds, setBounds] = useState<DayBounds>(() => computeTodayBounds());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const refreshBounds = () => {
      setBounds(computeTodayBounds());
    };

    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = Math.max(0, nextMidnight.getTime() - now.getTime());

    let dailyInterval: number | undefined;

    const midnightTimeout = window.setTimeout(() => {
      refreshBounds();
      dailyInterval = window.setInterval(refreshBounds, ONE_DAY_MS);
    }, msUntilMidnight);

    return () => {
      window.clearTimeout(midnightTimeout);
      if (dailyInterval !== undefined) {
        window.clearInterval(dailyInterval);
      }
    };
  }, []);

  return bounds;
};

const decorateOrders = (orders: KitchenOrder[]): DecoratedExpoOrder[] =>
  orders.map((order) => {
    const totals = order.items.reduce(
      (acc, item) => {
        const readyCount = item.readyQuantity ?? (item.status === 'READY' ? item.quantity : 0);

        return {
          total: acc.total + item.quantity,
          ready: acc.ready + readyCount,
        };
      },
      { total: 0, ready: 0 },
    );

    const hasReadyItems = order.items.some((item) => item.status === 'READY' || item.status === 'PARTIAL_READY');
    const awaitingAssembly = order.status !== 'READY' && order.status !== 'COMPLETED' && hasReadyItems;
    const allItemsReady = totals.total > 0 && totals.ready >= totals.total;
    const elapsedSeconds = order.kds.elapsedSeconds ?? null;
    const overdue = elapsedSeconds !== null && elapsedSeconds > ASSEMBLY_TARGET_SECONDS;

    return {
      order,
      totalQuantity: totals.total,
      readyQuantity: totals.ready,
      awaitingAssembly,
      allItemsReady,
      overdue,
    } satisfies DecoratedExpoOrder;
  });

const ExpoPage = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const activeRestaurantId = useTenantStore((state) => state.restaurant?.id ?? null);

  const todayBounds = useTodayBounds();

  const ordersQuery = useQuery({
    queryKey: ['expo', 'orders'],
    queryFn: () => fetchKitchenOrders({ status: 'ALL' }),
    refetchInterval: 20000,
  });

  const ordersData = ordersQuery.data?.orders;
  const orders = useMemo(() => ordersData ?? EMPTY_KITCHEN_ORDERS, [ordersData]);
  const decorated = useMemo(() => decorateOrders(orders), [orders]);

  const assemblingOrders = useMemo(
    () => decorated.filter((entry) => entry.awaitingAssembly && entry.order.status !== 'COMPLETED'),
    [decorated],
  );
  const readyOrders = useMemo(
    () => decorated.filter((entry) => entry.order.status === 'READY'),
    [decorated],
  );
  const completedOrders = useMemo(() => {
    const { startMs, endMs } = todayBounds;

    const isWithinToday = (iso?: string): boolean => {
      if (!iso) {
        return false;
      }
      const parsed = new Date(iso);
      if (!safeDate(parsed)) {
        return false;
      }
      const value = parsed.getTime();
      return value >= startMs && value < endMs;
    };

    return decorated
      .filter((entry) => {
        if (entry.order.status !== 'COMPLETED') {
          return false;
        }

        if (isWithinToday(entry.order.updatedAt)) {
          return true;
        }

        if (isWithinToday(entry.order.placedAt)) {
          return true;
        }

        return false;
      })
      .sort((a, b) => (b.order.sequenceMicros ?? 0) - (a.order.sequenceMicros ?? 0))
      .slice(0, 6);
  }, [decorated, todayBounds]);

  const overdueCount = useMemo(
    () => decorated.filter((entry) => entry.overdue && entry.order.status !== 'COMPLETED').length,
    [decorated],
  );

  const markReadyMutation = useMutation({
    mutationFn: (orderId: string) => updateKitchenOrderStatus(orderId, 'READY'),
    onSuccess: () => {
      toast({ title: 'Order assembled', description: 'Expo marked the order ready for pickup.' });
      queryClient.invalidateQueries({ queryKey: ['expo', 'orders'] });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Failed to update order',
        description: error instanceof Error ? error.message : 'Try again in a moment.',
        variant: 'destructive',
      });
    },
  });

  const completeMutation = useMutation({
    mutationFn: (orderId: string) => updateKitchenOrderStatus(orderId, 'COMPLETED'),
    onSuccess: () => {
      toast({ title: 'Order completed', description: 'Order moved out of the expo queue.' });
      queryClient.invalidateQueries({ queryKey: ['expo', 'orders'] });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Completion failed',
        description: error instanceof Error ? error.message : 'Try again shortly.',
        variant: 'destructive',
      });
    },
  });

  const notifyMutation = useMutation({
    mutationFn: (orderId: string) => notifyKitchenOrder(orderId),
    onSuccess: () => {
      toast({ title: 'Runner notified', description: 'We pinged the floor to pick up the order.' });
      queryClient.invalidateQueries({ queryKey: ['expo', 'orders'] });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Notification failed',
        description: error instanceof Error ? error.message : 'Unable to notify runner.',
        variant: 'destructive',
      });
    },
  });

  useEffect(() => {
    if (!activeRestaurantId) {
      return;
    }

    const channel = supabase
      .channel(`kitchen-orders-${activeRestaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'orders',
          filter: `restaurant_id=eq.${activeRestaurantId}`,
        },
        () => queryClient.invalidateQueries({ queryKey: ['expo', 'orders'] }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurantId, queryClient]);

  const metrics = [
    {
      label: 'Active Orders',
      value: assemblingOrders.length + readyOrders.length,
    },
    {
      label: 'Awaiting Assembly',
      value: assemblingOrders.length,
    },
    {
      label: 'Ready for Pickup',
      value: readyOrders.length,
    },
    {
      label: 'Over SLA',
      value: overdueCount,
    },
  ];

  return (
    <div className="px-6 pt-6 pb-12">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col gap-8">
        <header className="space-y-3">
          <div className="flex flex-col gap-2">
            <h1 className="flex items-center gap-3 text-3xl font-bold text-foreground">
              <Package className="h-8 w-8 text-primary" />
              Expo Assemble
            </h1>
            <p className="text-muted-foreground">
              Track ready items, finish assembly, and release orders to runners with real-time kitchen updates.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map((metric) => (
              <div
                key={metric.label}
                className="rounded-xl border border-muted bg-card/70 p-4 shadow-sm backdrop-blur"
              >
                <p className="text-sm text-muted-foreground">{metric.label}</p>
                <p className="mt-1 text-2xl font-semibold text-foreground">{metric.value}</p>
              </div>
            ))}
          </div>
        </header>

        {ordersQuery.isLoading ? (
          <div className="flex flex-1 items-center justify-center py-32">
            <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
              <Loader2 className="h-10 w-10 animate-spin" />
              <p className="text-sm">Loading expo queue…</p>
            </div>
          </div>
        ) : null}

        {ordersQuery.isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 p-6 text-destructive">
            <p className="font-medium">We couldn&apos;t load the expo queue.</p>
            <p className="text-sm opacity-80">Check your connection and retry.</p>
          </div>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[1fr_1fr_0.7fr]">
          <section className="flex flex-col gap-4 rounded-2xl border border-muted bg-card/60 p-5 backdrop-blur">
            <header className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Needs Assembly</h2>
                <p className="text-sm text-muted-foreground">Items are landing from the line-finish merging before release.</p>
              </div>
              <Badge variant={assemblingOrders.length > 0 ? 'default' : 'secondary'}>{assemblingOrders.length}</Badge>
            </header>

            <div className="space-y-4">
              {assemblingOrders.length === 0 ? (
                <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
                  No orders are waiting for assembly.
                </div>
              ) : null}

              {assemblingOrders.map((entry) => (
                <ExpoOrderCard
                  key={entry.order.id}
                  entry={entry}
                  variant="assembling"
                  onMarkReady={() => markReadyMutation.mutate(entry.order.id)}
                  markReadyPending={markReadyMutation.isPending}
                />
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-4 rounded-2xl border border-muted bg-card/60 p-5 backdrop-blur">
            <header className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Ready for Pickup</h2>
                <p className="text-sm text-muted-foreground">Staged orders waiting on runners or customer handoff.</p>
              </div>
              <Badge variant={readyOrders.length > 0 ? 'default' : 'secondary'}>{readyOrders.length}</Badge>
            </header>

            <div className="space-y-4">
              {readyOrders.length === 0 ? (
                <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
                  No staged orders right now.
                </div>
              ) : null}

              {readyOrders.map((entry) => (
                <ExpoOrderCard
                  key={entry.order.id}
                  entry={entry}
                  variant="ready"
                  onNotify={() => notifyMutation.mutate(entry.order.id)}
                  onComplete={() => completeMutation.mutate(entry.order.id)}
                  notifyPending={notifyMutation.isPending}
                  completePending={completeMutation.isPending}
                />
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-4 rounded-2xl border border-muted bg-card/60 p-5 backdrop-blur">
            <header className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Recently Completed</h2>
                <p className="text-sm text-muted-foreground">Quick snapshot of orders that just cleared expo.</p>
              </div>
              <Badge variant="secondary">{completedOrders.length}</Badge>
            </header>

            <div className="space-y-4">
              {completedOrders.length === 0 ? (
                <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
                  Completed orders will appear here for quick reference.
                </div>
              ) : null}

              {completedOrders.map((entry) => (
                <ExpoOrderCard key={entry.order.id} entry={entry} variant="completed" />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

interface ExpoOrderCardProps {
  entry: DecoratedExpoOrder;
  variant: 'assembling' | 'ready' | 'completed';
  onMarkReady?: () => void;
  onNotify?: () => void;
  onComplete?: () => void;
  markReadyPending?: boolean;
  notifyPending?: boolean;
  completePending?: boolean;
}

const ExpoOrderCard = ({
  entry,
  variant,
  onMarkReady,
  onNotify,
  onComplete,
  markReadyPending,
  notifyPending,
  completePending,
}: ExpoOrderCardProps): JSX.Element => {
  const { order, readyQuantity, totalQuantity, allItemsReady, overdue } = entry;
  const elapsedLabel = formatEta(order.kds.elapsedSeconds);
  const pickupWindow = order.scheduledWindowLabel ?? null;
  const progressPercent = totalQuantity > 0 ? Math.min(100, Math.round((readyQuantity / totalQuantity) * 100)) : 0;

  return (
    <div
      className={cn(
        'rounded-xl border border-muted bg-background/80 p-5 shadow-sm backdrop-blur transition hover:border-primary/40',
        overdue ? 'border-amber-400/60' : null,
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-foreground">{order.code ?? '-'}</span>
            {order.kds.priority && order.kds.priority !== 'NORMAL' ? (
              <Badge variant="destructive">{order.kds.priority}</Badge>
            ) : null}
            {overdue ? (
              <span className="inline-flex items-center gap-1 text-sm text-amber-300">
                <AlertTriangle className="h-4 w-4" />
                SLA
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            Placed {formatPlacedAt(order.placedAt)}
            {pickupWindow ? ` • Pickup ${pickupWindow}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>{elapsedLabel ?? 'Just now'}</span>
        </div>
      </header>

      <div className="mt-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {readyQuantity}/{totalQuantity} items ready
          </span>
          <span>{progressPercent}%</span>
        </div>
        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-2 rounded-full bg-primary" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {order.items.map((item) => (
          <ItemRow key={item.id ?? `${item.menuItemId}-${item.name}`} item={item} />
        ))}
      </div>

      <footer className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="secondary">{order.type?.toUpperCase() ?? 'ORDER'}</Badge>
          {order.source ? <span className="uppercase tracking-wide">{order.source}</span> : null}
        </div>
        {variant === 'assembling' ? (
          <Button
            type="button"
            size="sm"
            disabled={!allItemsReady || markReadyPending}
            onClick={onMarkReady}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {markReadyPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Finish assembly
          </Button>
        ) : null}
        {variant === 'ready' ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={notifyPending}
              onClick={onNotify}
            >
              {notifyPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bell className="mr-2 h-4 w-4" />}
              Notify runner
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={completePending}
              onClick={onComplete}
              className="bg-emerald-500 text-emerald-950 hover:bg-emerald-400"
            >
              {completePending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              Complete order
            </Button>
          </div>
        ) : null}
      </footer>
    </div>
  );
};

const ItemRow = ({ item }: { item: KitchenOrderItem }): JSX.Element => {
  const statusLabel = item.status.replace('_', ' ').toLowerCase();
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-muted/40 bg-background/60 p-3">
      <div className="flex flex-1 flex-col gap-1">
        <p className="text-sm font-medium text-foreground">
          {item.quantity}× {item.name}
        </p>
        {item.notes ? <p className="text-xs text-muted-foreground">{item.notes}</p> : null}
      </div>
      <span className={cn('text-xs font-semibold uppercase tracking-wide', ITEM_STATUS_COLORS[item.status])}>
        {statusLabel}
      </span>
    </div>
  );
};

export default ExpoPage;
